// ----------------------------------------------------------------------- react
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- internos
import {
  TIPOS_VALIDOS,
  archivoUrl,
  corregir,
  descartar,
  traerComprobantes,
} from "../services/comprobantes";
import { plata } from "../shared/plata";
import { etiquetaLeidoPor } from "../shared/lectura";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "./css/comprobantes.css";

/* Los comprobantes que se mandan por WhatsApp, acá.
 *
 * ── DE DÓNDE SALIÓ ──────────────────────────────────────────────────────────
 *
 * Igna, 11/09: *«¿dónde se guarda el remito? ¿lo puedo ver en el Hub?»*. No se
 * podía: los datos estaban en `comprobantes` desde el 11/08 y no había ninguna
 * pantalla — la única forma de verlos era preguntarle al bot por WhatsApp.
 *
 * ── LO QUE NO SE LEYÓ SE VE ─────────────────────────────────────────────────
 *
 * Un campo que el modelo no pudo leer entra en null, y acá dice «no lo pude
 * leer». En blanco se leería igual que un papel que no lo trae, y son dos
 * cosas distintas: una manda a mirar la foto, la otra no.
 *
 * Lo mismo con `dudas`: son los campos de los que el modelo no está seguro. Un
 * número leído con duda y uno leído bien se ven idénticos si no se dice cuál
 * es cuál — y el único momento barato para corregirlo es con el papel cerca.
 *
 * ── Y EL ARCHIVO QUE FALTA SE VE MÁS ────────────────────────────────────────
 *
 * `archivo` en null no es «sin foto»: es una fila cuyo papel no llegó a
 * guardarse. Sin el original al lado, los cuatro campos no se pueden verificar
 * contra nada, así que la fila se marca — no se esconde ni se dibuja como si
 * estuviera completa.
 *
 * ── Y EL CARTEL DICE QUÉ HACER, NO SÓLO QUÉ PASÓ ────────────────────────────
 *
 * Decía que eran «los de antes del 11/09, cuando el depósito no existía» y que
 * de esas fotos «sólo hay copia en el WhatsApp de quien las mandó». Las dos
 * mitades envejecieron mal el mismo día: uno de los seis entró EL 11/09 a las
 * 10:48 —dos horas y media antes de que se creara el bucket—, así que la fecha
 * ya no explicaba nada; y desde que un reenvío completa la fila en vez de
 * rebotar por duplicado, la copia del WhatsApp dejó de ser un consuelo y pasó
 * a ser el arreglo.
 *
 * Una causa contada con una fecha se vuelve falsa sola. Un cartel que dice qué
 * hacer sigue sirviendo.
 *
 * ── UNA LISTA VACÍA NO ES «NO HAY» ──────────────────────────────────────────
 *
 * El recorte lo hace la RLS y no esta pantalla: desde el 15/09 se ve lo del
 * propio rol —el taller ve lo del taller— más lo que cargó uno mismo. Vacío
 * quiere decir «no hay ninguno para tu rol», y decirlo así es la diferencia
 * entre entender la pantalla y creer que el sistema perdió todo.
 *
 * ── EL CAJÓN SE CORRIGE Y SE RECLASIFICA ────────────────────────────────────
 *
 * Igna, 15/09, mirando la foto de unos guantes archivada como comprobante:
 * *«me gusta la idea del cajón genérico y que se pueda "mandar" a donde
 * corresponde al igual que editarlo por si hubo algo que no leyó. Como con los
 * vales»*.
 *
 * Lo que llega entra igual: el bot NUNCA rechaza un archivo, porque el papel
 * es lo único que no se puede reponer y un lector que decide qué guardar
 * pierde justo lo raro. Lo que faltaba era la otra mitad — el cajón se ordena
 * DESPUÉS, con el papel al lado:
 *
 *   · **corregir** lo que el modelo leyó mal o no leyó;
 *   · **mandarlo a donde corresponde**, que acá es el tipo: lo que entró como
 *     «otro» y en realidad es una factura pasa a facturas;
 *   · **«no es un comprobante»**, que lo saca de la lista sin borrar nada.
 *
 * DESCARTAR NO ES BORRAR y por eso hay una pestaña para verlos. Un descarte
 * que desaparece es un borrado con otro nombre, y el que descarta hoy no es el
 * que va a buscar esa foto en tres meses.
 *
 * LO CORREGIDO SE NOTA. `revisado_por` se escribe en cada corrección: un campo
 * leído por un modelo y uno escrito por una persona con el papel en la mano no
 * valen lo mismo, y guardados iguales no hay forma de saber cuál es cuál.
 */

const TZ = "America/Argentina/Buenos_Aires";

const TIPOS = [
  ["todos", "Todos"],
  ["remito", "Remitos"],
  ["factura", "Facturas"],
  ["presupuesto", "Presupuestos"],
  ["otro", "Otros"],
  ["descartados", "Descartados"],
];

const ROTULO_TIPO = {
  factura: "Factura", remito: "Remito",
  presupuesto: "Presupuesto", otro: "Otro",
};

/* Los campos que se corrigen, con el rótulo que ya usa la ficha de arriba: el
   que corrige acaba de leer «CUIT · no lo pude leer» y tiene que encontrar ese
   mismo nombre abajo. */
const EDITABLES = [
  ["proveedor", "Proveedor", "text"],
  ["cuit", "CUIT", "text"],
  ["fecha", "Fecha", "date"],
  ["numero", "Número", "text"],
  ["importe", "Importe", "number"],
  ["moneda", "Moneda", "text"],
  ["detalle", "Detalle", "text"],
];

/* Sin `new Date(iso)`: una fecha sola («2026-09-11») la parsea como UTC y en
 * Argentina se dibuja el día anterior. Se parte el texto, que es lo que hay. */
export const dia = (f) => {
  const s = String(f || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const cuando = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-AR", {
        timeZone: TZ, day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "";

/* Un renglón del papel. `null` se dibuja como el hueco que es. */
const Dato = ({ rotulo, valor }) => (
  <div className="cmp-dato">
    <span className="cmp-rotulo">{rotulo}</span>
    {valor === null || valor === undefined || valor === "" ? (
      <span className="tenue">no lo pude leer</span>
    ) : (
      <b>{valor}</b>
    )}
  </div>
);

/* El formulario de corrección. Vive al lado del papel y no en un modal: lo que
   se corrige se lee de la foto que está a la izquierda, y un modal la tapa.
   Se exporta para reusarlo en la vista unificada «Facturas y Remitos». */
export function Corregir({ f, onListo, onCancelar }) {
  const [b, setB] = useState({
    tipo: f.tipo || "otro",
    proveedor: f.proveedor || "",
    cuit: f.cuit || "",
    fecha: String(f.fecha || "").slice(0, 10),
    numero: f.numero || "",
    importe: f.importe === null || f.importe === undefined ? "" : String(f.importe),
    moneda: f.moneda || "",
    detalle: f.detalle || "",
    privada: f.alcance === "privado",
  });
  const [error, setError] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const campo = (k, v) => setB((x) => ({ ...x, [k]: v }));

  /* SÓLO VIAJA LO QUE CAMBIÓ. Mandar los ocho campos en cada guardado pisaría
     con "" un dato que otro acaba de corregir en la otra pestaña, y además
     dejaría fuera de `dudas` campos que nadie miró. */
  const cambiados = () => {
    const out = {};
    const antes = {
      tipo: f.tipo || "otro", proveedor: f.proveedor || "", cuit: f.cuit || "",
      fecha: String(f.fecha || "").slice(0, 10), numero: f.numero || "",
      importe: f.importe === null || f.importe === undefined ? "" : String(f.importe),
      moneda: f.moneda || "", detalle: f.detalle || "",
    };
    for (const k of Object.keys(antes)) {
      if (String(b[k] ?? "") === String(antes[k])) continue;
      const v = String(b[k] ?? "").trim();
      /* Vacío vuelve a ser `null` y no "": null es «el papel no lo trae» y ""
         se dibujaría como un dato que existe y está en blanco. */
      out[k] = v === "" ? null : (k === "importe" ? Number(v) : v);
    }
    const alcance = b.privada ? "privado" : "roles";
    if (alcance !== (f.alcance || "roles")) out.alcance = alcance;
    return out;
  };

  const guardar = async () => {
    const parche = cambiados();
    if (!Object.keys(parche).length) { onCancelar(); return; }
    setError("");
    setTrabajando(true);
    try {
      await corregir(f.id, parche, Array.isArray(f.dudas) ? f.dudas : []);
      onListo();
    } catch (e) {
      setError(e?.message || "No se pudo guardar.");
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <div className="cmp-editar">
      <label className="cmp-campo">
        Qué es
        <select value={b.tipo} aria-label="Tipo"
                onChange={(e) => campo("tipo", e.target.value)}>
          {TIPOS_VALIDOS.map((t) => (
            <option key={t} value={t}>{ROTULO_TIPO[t] || t}</option>
          ))}
        </select>
      </label>

      {EDITABLES.map(([k, rotulo, tipo]) => (
        <label className="cmp-campo" key={k}>
          {rotulo}
          <input type={tipo} value={b[k]} aria-label={rotulo}
                 onChange={(e) => campo(k, e.target.value)} />
        </label>
      ))}

      <label className="cmp-campo cmp-campo--check">
        <input type="checkbox" checked={b.privada} aria-label="Privada"
               onChange={(e) => campo("privada", e.target.checked)} />
        {" "}Privada — no la ve nadie más
      </label>

      {error && <p className="cmp-aviso">{error}</p>}

      <div className="cmp-acciones">
        <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                onClick={onCancelar}>
          Cancelar
        </button>
        <button type="button" className="tk-boton tk-boton--chico"
                disabled={trabajando} onClick={guardar}>
          {trabajando ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

const LLAVES = ["ver.comprobantes", "ver.comprobantes.todos"];

const Comprobantes = () => {
  const { permisos = null } = useOutletContext() || {};
  const puedeEditar = permisos !== null
    && permisos.includes("cargar.comprobantes");
  /* Las mismas llaves que el menú (`secciones.js`): el que llega por la URL
     sin ninguna encuentra la puerta cerrada. `null` es «todavía no se sabe». */
  const puedeVer = permisos === null
    || LLAVES.some((ll) => permisos.includes(ll));

  const [datos, setDatos] = useState(undefined);
  const [error, setError] = useState(null);
  const [tipo, setTipo] = useState("todos");
  const [busca, setBusca] = useState("");
  const [links, setLinks] = useState({}); // id -> url firmada
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState("");
  const [vuelta, setVuelta] = useState(0);

  useEffect(() => {
    let vivo = true;
    traerComprobantes()
      .then((filas) => {
        if (!vivo) return;
        setDatos(filas);
        filas.forEach((f) => {
          if (!f.archivo) return;
          archivoUrl(f.archivo).then((u) => {
            if (vivo) setLinks((prev) => ({ ...prev, [f.id]: u }));
          });
        });
      })
      /* `traerTodas` tira si la base deja de mandar a mitad de camino. Ese
       * error se MUESTRA: media lista de facturas con cara de completa es
       * peor que una pantalla que no abre. */
      .catch((e) => vivo && setError(e?.message || "No se pudieron leer."));
    return () => { vivo = false; };
  }, [vuelta]);

  const recargar = () => { setEditando(null); setVuelta((v) => v + 1); };

  const tirar = async (f, fuera) => {
    setAviso("");
    try {
      await descartar(f.id, fuera);
      recargar();
    } catch (e) {
      setAviso(e?.message || "No se pudo guardar.");
    }
  };

  const visibles = useMemo(() => {
    if (!datos) return [];
    const t = busca.trim().toLowerCase();
    return datos.filter((f) => {
      /* LOS DESCARTADOS NO SE MEZCLAN CON EL RESTO, y tampoco desaparecen:
         tienen su pestaña. Un descarte que no se puede volver a ver es un
         borrado con otro nombre. */
      if (tipo === "descartados") {
        if (!f.anulado_en) return false;
      } else {
        if (f.anulado_en) return false;
        if (tipo !== "todos" && (f.tipo || "otro") !== tipo) return false;
      }
      if (!t) return true;
      return [f.proveedor, f.numero, f.cuit, f.detalle, f.quien_nombre]
        .some((v) => String(v || "").toLowerCase().includes(t));
    });
  }, [datos, tipo, busca]);

  const enElCajon = (datos || []).filter((f) => !f.anulado_en);
  const sinArchivo = enElCajon.filter((f) => !f.archivo).length;
  const descartados = (datos || []).length - enElCajon.length;

  if (!puedeVer) return <PuertaCerrada />;
  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <div className="cmp">
      <p className="tk-meta">
        Lo que se manda por WhatsApp a Cepi: facturas, remitos y presupuestos.
        Mandá la foto y queda acá. <b>Ves lo de tu rol y lo que cargaste vos</b>
        {" "}— y lo que se mandó con la palabra «privada» lo ve sólo quien lo
        cargó.
      </p>

      <div className="cmp-barra">
        <div className="cmp-tipos" role="group" aria-label="Tipo de comprobante">
          {TIPOS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={"cmp-seg" + (tipo === id ? " activo" : "")}
              onClick={() => setTipo(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="cmp-busca"
          placeholder="Proveedor, número, CUIT…"
          aria-label="Buscar comprobantes"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {/* LO QUE SE PERDIÓ SE CUENTA ARRIBA. Repartido de a una fila, «sin
          archivo» se lee como un detalle de esa fila; junto se lee como lo
          que es: un agujero que tuvo una causa y una fecha. */}
      {sinArchivo > 0 && (
        <p className="cmp-aviso">
          ⚠️ {sinArchivo}{" "}
          {sinArchivo === 1 ? "comprobante no tiene" : "comprobantes no tienen"}{" "}
          el archivo guardado: quedaron los datos y no quedó el papel.{" "}
          <b>Se puede reponer</b> — reenviale la foto a Cepi por WhatsApp y la
          adjunta a la fila que ya está, sin duplicarla y sin tocar los datos.
        </p>
      )}

      {aviso && <p className="cmp-aviso">{aviso}</p>}

      {datos === undefined && <p className="tenue">Buscando…</p>}

      {datos !== undefined && visibles.length === 0 && (
        <div className="panel-aviso suave">
          <h3>
            {tipo === "descartados"
              ? "No se descartó ninguno"
              : enElCajon.length === 0
                ? "Todavía no hay ningún comprobante"
                : "Nada con ese filtro"}
          </h3>
          {/* EL CAJÓN VACÍO CON DESCARTADOS ADENTRO NO ES UN CAJÓN VACÍO. Sin
              esta línea, descartar el último comprobante deja una pantalla
              que dice «todavía no hay ninguno» — o sea, que se perdieron. */}
          {tipo !== "descartados" && descartados > 0 && (
            <p className="panel-aviso__chico">
              Hay {descartados}{" "}
              {descartados === 1 ? "descartado" : "descartados"}:{" "}
              <button type="button" className="cmp-link"
                      onClick={() => setTipo("descartados")}>
                verlos
              </button>.
            </p>
          )}
        </div>
      )}

      {visibles.map((f) => {
        const tipoF = (f.tipo || "otro").charAt(0).toUpperCase()
          + (f.tipo || "otro").slice(1);
        const dudas = Array.isArray(f.dudas) ? f.dudas : [];
        return (
          <div className="cmp-carta" key={f.id}>
            <div className="cmp-papel">
              {links[f.id] ? (
                <a href={links[f.id]} target="_blank" rel="noreferrer">
                  <img
                    src={links[f.id]}
                    alt={`${tipoF} de ${f.proveedor || "proveedor sin leer"}`}
                  />
                </a>
              ) : f.archivo ? (
                <span className="tenue">Abriendo el archivo…</span>
              ) : (
                <span className="cmp-sinpapel">
                  Sin archivo guardado
                  <small>Quedaron sólo los datos de abajo</small>
                </span>
              )}
            </div>

            <div className="cmp-datos">
              <div className="cmp-cabeza">
                <b>{tipoF}</b>
                {f.proveedor ? ` · ${f.proveedor}` : ""}
                {f.alcance === "privado" && (
                  <span className="pill gris">🔒 privada</span>
                )}
                {!f.archivo && <span className="pill mal">sin archivo</span>}
                {f.anulado_en && (
                  <span className="pill gris">descartado</span>
                )}
                {f.revisado_en && (
                  <span className="pill ok" title={f.revisado_por || ""}>
                    ✓ revisado
                  </span>
                )}
              </div>

              <Dato rotulo="Proveedor" valor={f.proveedor} />
              <Dato rotulo="CUIT" valor={f.cuit} />
              <Dato rotulo="Fecha" valor={dia(f.fecha)} />
              <Dato rotulo="Número" valor={f.numero} />
              {/* Un remito no suele traer importe: dibujar «no lo pude leer»
                  ahí manda a buscar un número que el papel no tiene. */}
              {!(f.tipo === "remito" && f.importe === null) && (
                <Dato rotulo="Importe" valor={plata(f.importe, f.moneda)} />
              )}

              {f.detalle && <p className="cmp-detalle">{f.detalle}</p>}

              {dudas.length > 0 && (
                <p className="cmp-dudas">
                  ⚠️ El bot no está seguro de: {dudas.join(", ")}.
                </p>
              )}

              <p className="cmp-pie tenue">
                {f.quien_nombre || "alguien"} · {cuando(f.creado_en)}
                {f.leido_por ? ` · ${etiquetaLeidoPor(f.leido_por)}` : ""}
                {/* QUIÉN LO CORRIGIÓ VA AL LADO DE QUIÉN LO LEYÓ. Un campo
                    escrito por una persona con el papel en la mano y uno
                    leído por un modelo no valen lo mismo. */}
                {f.revisado_por ? ` · corrigió ${f.revisado_por}` : ""}
              </p>

              {puedeEditar && editando !== f.id && (
                <div className="cmp-acciones">
                  <button type="button"
                          className="tk-boton tk-boton--chico"
                          onClick={() => { setAviso(""); setEditando(f.id); }}>
                    ✎ Corregir
                  </button>
                  {f.anulado_en ? (
                    <button type="button"
                            className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => tirar(f, false)}>
                      ↩ Devolver al cajón
                    </button>
                  ) : (
                    <button type="button"
                            className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => tirar(f, true)}>
                      No es un comprobante
                    </button>
                  )}
                </div>
              )}

              {puedeEditar && editando === f.id && (
                <Corregir f={f} onListo={recargar}
                          onCancelar={() => setEditando(null)} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Comprobantes;
